/** @jsxImportSource @emotion/react */
import type { ReactElement } from 'react';
import React from 'react';
import type { Interpolation } from 'styled-components';
import type { Theme } from '@mui/material';
import { useTheme } from '@mui/material';
import Card from '../../../../component_library/components/Card';
import HtmlBox from '../../../../component_library/components/HtmlBox';
import type { MediaType } from '../../../../component_library/components/Card/Card';
import { PlayIcon } from '../../../../component_library/components/VideoPlayer';
import { type IPropsWithTestId } from '../../../../constants/data-element-tracking-ids';
import {
  ResourceType,
  useTranslateResourceType,
} from '../../../../dotcms/dotcms-models/resources/resource-with-thumbnail/resource-type';
import { useGetTheme } from '../../../../hooks/use-get-theme';
import { useSettingBasedOnBreakpoints } from '../../../../hooks/use-setting-based-on-breakpoints';
import type { HtmlFromDotcms } from '../../../../dotcms/dotcms-models/converters-from-dotcms';
import { useThemeToken } from '../../../../hooks/use-get-theme/use-get-theme';

interface IProps extends IPropsWithTestId {
  renderCardWrapper?: (children: ReactElement) => ReactElement;
  resourceType: ResourceType;
  resourceLink?: IPropsWithTestId & {
    linkText: string;
    linkTo: string;
  };
  media: {
    url: string;
    html?: HtmlFromDotcms;
    type: MediaType;
  };
  label?: string;
  title?: HtmlFromDotcms | string;
  description?: HtmlFromDotcms | string;
  uniqueId?: string;
}

const ResourcePreviewCard: React.FC<IProps> = ({
  renderCardWrapper,
  resourceType,
  resourceLink,
  media,
  label,
  title,
  description,
  uniqueId,
  dataElementId,
}) => {
  const theme = useGetTheme();
  const muiTheme = useTheme();
  const themeToken = useThemeToken();
  const { isMobile } = useSettingBasedOnBreakpoints(muiTheme);
  const { translateResourceType } = useTranslateResourceType();

  const cardStyles: Interpolation<Theme> = {
    textAlign: 'left',
    maxWidth: !isMobile ? '550px' : '100%',
    backgroundColor: theme.colors.grayscale.white,
  };

  const card = (
    <Card
      dataElementId={dataElementId}
      uniqueId={uniqueId}
      label={label ?? translateResourceType(resourceType) ?? ''}
      link={resourceLink}
      titleElement={
        typeof title === 'string' ? title : <HtmlBox html={title} />
      }
      mediaProps={{
        ...media,
        iconComponent:
          resourceType === ResourceType.Video ? (
            <PlayIcon theme={theme} />
          ) : null,
      }}
      theme={theme}
      themeToken={themeToken}
      additionalStyles={cardStyles}
    >
      {typeof description === 'string' ? (
        description
      ) : (
        <HtmlBox html={description} />
      )}
    </Card>
  );

  if (renderCardWrapper) {
    return renderCardWrapper(card);
  }

  return card;
};

export default ResourcePreviewCard;
